<template>
  <!-- Status Modal -->
  <div v-if="gameState.inStatusModal" class="status-modal" @click="closeModal">
    <div class="status-card" @click.stop>
      <!-- Fixed Header -->
      <div class="status-header">
        <h3>角色状态</h3>
        <p>修炼之路漫漫，勇往直前！</p>
      </div>

      <!-- Scrollable Content -->
      <div class="status-content">
        <!-- Character Info & Realm Progress -->
        <div class="character-section">
          <div class="character-info-row">
            <span class="character-name">{{ gameState.name }}</span>
            <span class="spiritual-root-name" :class="spiritualRootClass">{{ gameState.spiritualRoot.name }}</span>
            <span class="character-sect">{{ gameState.sect }}</span>
            <span class="character-location">{{ gameState.location }}</span>
          </div>

          <div class="realm-progress-section">
            <div class="realm-display">
              <span class="realm-text">{{ realmName }}{{ substageName }}</span>
              <span class="period-text">({{ periodName }})</span>
            </div>
            <div class="realm-bar">
              <div class="realm-fill" :style="{ width: progressPercent + '%' }"></div>
              <span class="realm-value">{{ gameState.xp }}/{{ xpToLevel }}</span>
            </div>
          </div>
        </div>

        <!-- Core Stats -->
        <div class="stats-section">
          <div class="stat-row">
            <div class="stat-item">
              <div class="stat-label-row">
                <span class="stat-label">气血</span>
                <span v-if="injuryType" class="status-badge" :class="injuryType === '重伤' ? 'severe-injury-badge' : 'injury-badge'">
                  {{ injuryType }}
                </span>
                <span class="stat-value">{{ gameState.qi }}/{{ effectiveMaxQi }}</span>
              </div>
              <div class="stat-bar">
                <div class="stat-fill qi-fill" :style="{ width: qiPercent + '%' }"></div>
              </div>
              <div v-if="hasInjury" class="injury-info">
                <span class="injury-text">原始上限: {{ gameState.originalMaxQi || gameState.maxQi }}</span>
                <span class="injury-reduction">(-{{ injuryReduction }})</span>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-label-row">
                <span class="stat-label">灵力</span>
                <span class="stat-value">{{ gameState.mana }}/{{ maxMana }}</span>
              </div>
              <div class="stat-bar">
                <div class="stat-fill mana-fill" :style="{ width: manaPercent + '%' }"></div>
              </div>
            </div>
          </div>

          <div class="stat-row">
            <div class="stat-item">
              <span class="stat-label">攻击</span>
              <span class="stat-value">{{ gameState.attack }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">防御</span>
              <span class="stat-value">{{ gameState.defense }}</span>
            </div>
          </div>

          <div class="stat-row">
            <div class="stat-item">
              <span class="stat-label">神识</span>
              <span class="stat-value">{{ gameState.divineSenseStrength }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">暴击</span>
              <span class="stat-value">5%</span>
            </div>
          </div>
        </div>


        <!-- Refining Levels -->
        <div class="refining-section">
          <h4>炼丹炼器</h4>
          <div class="refining-list">
            <div class="refining-item">
              <span class="refining-name">炼丹等级</span>
              <span class="refining-level">{{ pillRefiningLevelName }}</span>
            </div>
            <div class="refining-item">
              <span class="refining-name">炼器等级</span>
              <span class="refining-level">{{ treasureRefiningLevelName }}</span>
            </div>
          </div>
        </div>

        <!-- Realm Progress System -->
        <div class="realm-system-section">
          <h4>境界系统</h4>
          <div class="realm-progress">
            <div
              v-for="realm in realmNames"
              :key="realm.name"
              class="realm-item"
              :class="{ 'current-realm': realm.index === gameState.realm }"
            >
              <div class="realm-name">
                {{ realm.name }}境界
                <span v-if="realm.index === gameState.realm" class="current-marker">(当前)</span>
              </div>
              <div v-if="realm.index === gameState.realm" class="substage-progress">
                <div
                  v-for="substage in substages"
                  :key="substage.index"
                  class="substage-item"
                  :class="{ 'current-substage': substage.index === gameState.substage }"
                >
                  <div class="substage-name">{{ substage.name }}({{ substage.period }})</div>
                  <div v-if="substage.index === gameState.substage" class="progress-bar">
                    <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                    <div class="progress-text">{{ gameState.xp }}/{{ xpToLevel }}</div>
                  </div>
                  <div v-else-if="substage.index < gameState.substage" class="completed">已完成</div>
                  <div v-else class="locked">未解锁</div>
                </div>
              </div>
              <div v-else-if="realm.index < gameState.realm" class="realm-completed">
                ✓ 已完成 (累计修为: {{ getRealmTotalXp(realm.index) }})
              </div>
              <div v-else class="realm-locked">✗ 未解锁</div>
            </div>
          </div>
        </div>



        <!-- Forbidden Map Info -->
        <div v-if="gameState.inForbiddenMap" class="forbidden-section">
          <h4>禁地信息</h4>
          <div class="forbidden-info">
            <div class="forbidden-item">
              <span class="forbidden-label">位置:</span>
              <span class="forbidden-value">({{ gameState.playerPos.x }}, {{ gameState.playerPos.y }})</span>
            </div>
            <div class="forbidden-item">
              <span class="forbidden-label">进度:</span>
              <span class="forbidden-value">{{ exploredCount }}/{{ totalCells }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Fixed Footer -->
      <div class="status-footer">
        <button @click="closeModal" class="close-btn">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import { xpToLevel } from '../realms.js';

export default {
  name: 'StateModal',
  props: {
    gameState: {
      type: Object,
      required: true
    },
    stateManager: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      realmNames: [
        { index: 0, name: '炼气' },
        { index: 1, name: '筑基' },
        { index: 2, name: '金丹' },
        { index: 3, name: '元婴' },
        { index: 4, name: '化神' },
        { index: 5, name: '渡劫' },
        { index: 6, name: '真仙' }
      ],
      substages: [
        { index: 0, name: '一层', period: '前期' },
        { index: 1, name: '二层', period: '前期' },
        { index: 2, name: '三层', period: '前期' },
        { index: 3, name: '四层', period: '中期' },
        { index: 4, name: '五层', period: '中期' },
        { index: 5, name: '六层', period: '中期' },
        { index: 6, name: '七层', period: '后期' },
        { index: 7, name: '八层', period: '后期' },
        { index: 8, name: '九层', period: '后期' },
        { index: 9, name: '十层', period: '巅峰' }
      ],
      refiningLevelNames: ['凡阶', '黄阶', '玄阶', '地阶', '天阶']
    };
  },
  computed: {
    realmName() {
      return this.realmNames.find(r => r.index === this.gameState.realm)?.name || '未知';
    },
    substageName() {
      return this.substages.find(s => s.index === this.gameState.substage)?.name || '未知';
    },
    periodName() {
      return this.substages.find(s => s.index === this.gameState.substage)?.period || '未知';
    },
    xpToLevel() {
      return xpToLevel(this.gameState.realm * 10 + this.gameState.substage);
    },
    progressPercent() {
      const required = this.xpToLevel;
      return required > 0 ? Math.min((this.gameState.xp / required) * 100, 100) : 0;
    },
    qiPercent() {
      const maxQi = this.effectiveMaxQi;
      return maxQi > 0 ? Math.min((this.gameState.qi / maxQi) * 100, 100) : 0;
    },
    maxMana() {
      // 暂时使用mana作为最大值，可以后续调整
      return this.gameState.mana || 50;
    },
    manaPercent() {
      const maxMana = this.maxMana;
      return maxMana > 0 ? Math.min((this.gameState.mana / maxMana) * 100, 100) : 0;
    },

    pillRefiningLevelName() {
      return this.refiningLevelNames[this.gameState.pillRefiningLevel] || '未知';
    },
    treasureRefiningLevelName() {
      return this.refiningLevelNames[this.gameState.treasureRefiningLevel] || '未知';
    },
    exploredCount() {
      return (this.gameState.exploredCells || []).length;
    },
    totalCells() {
      return this.gameState.mapSize * this.gameState.mapSize;
    },
    spiritualRootClass() {
      const rootType = this.gameState.spiritualRoot?.type;
      switch (rootType) {
        case 'single': return 'spiritual-root-single';
        case 'mixed': return 'spiritual-root-mixed';
        case 'waste': return 'spiritual-root-waste';
        default: return 'spiritual-root-default';
      }
    },
    effectiveMaxQi() {
      let maxQi = this.gameState.maxQi;
      if (this.gameState.statusEffects) {
        Object.values(this.gameState.statusEffects).forEach(effect => {
          if (effect.effects && effect.effects.maxQi) {
            maxQi += effect.effects.maxQi;
          }
        });
      }
      return Math.max(1, maxQi); // 确保不会小于1
    },
    qiFormula() {
      const originalMaxQi = this.gameState.originalMaxQi || this.gameState.maxQi;
      const effectiveMaxQi = this.effectiveMaxQi;
      const reduction = originalMaxQi - effectiveMaxQi;

      if (reduction > 0) {
        return ` (${originalMaxQi}-${reduction}) ${this.gameState.qi}/${originalMaxQi}`;
      }
      return '';
    },

    hasSevereInjury() {
      return this.gameState.statusEffects && this.gameState.statusEffects['重伤'] !== undefined;
    },
    hasInjury() {
      // 只要有任何导致气血减少的状态，就显示徽章
      return this.injuryReduction > 0;
    },
    injuryType() {
      const reduction = this.injuryReduction;
      const originalMaxQi = this.gameState.originalMaxQi || this.gameState.maxQi;
      const reductionPercent = reduction / originalMaxQi;

      if (reductionPercent >= 0.3) {
        return '重伤';
      } else if (reductionPercent > 0) {
        return '经脉受损';
      }
      return null;
    },
    injuryReduction() {
      const originalMaxQi = this.gameState.originalMaxQi || this.gameState.maxQi;
      const effectiveMaxQi = this.effectiveMaxQi;
      return Math.max(0, originalMaxQi - effectiveMaxQi);
    }
  },
  methods: {
    getRealmTotalXp(realmIndex) {
      let total = 0;
      const maxSubstage = realmIndex < this.gameState.realm ? 9 : 0;
      for (let l = 0; l <= realmIndex * 10 + maxSubstage; l++) {
        total += xpToLevel(l);
      }
      return total;
    },
    closeModal() {
      this.gameState.inStatusModal = false;
    }
  }
};
</script>

<style scoped>
.status-modal {
  position: absolute;
  inset: 0px 0px 0px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 6, 23, 0.8);
  z-index: 250;
  backdrop-filter: blur(2px);
}

.status-card {
  background: #071328;
  border-radius: 12px;
  border: 1px solid #334155;
  color: #e6eef8;
  min-width: 400px;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.status-header {
  flex-shrink: 0;
  padding: 12px 20px 8px 20px;
  border-bottom: 1px solid #334155;
}

.status-content {
  flex: 1;
  overflow-y: auto;
  padding: 10px 16px;
}

.status-footer {
  flex-shrink: 0;
  padding: 10px 16px 12px 16px;
  border-top: 1px solid #334155;
  display: flex;
  justify-content: center;
}

.status-card h3 {
  margin: 0;
  font-size: 18px;
  color: #60a5fa;
  text-align: center;
  border-bottom: 1px solid #334155;
  padding-bottom: 8px;
}

.status-card p {
  margin: 0;
  color: #cbd5e1;
  font-size: 14px;
  text-align: center;
  margin-top: 8px;
}

.status-section {
  margin-bottom: 16px;
}

.status-section h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #34d399;
  border-bottom: 1px solid #334155;
  padding-bottom: 4px;
}

/* Character Section */
.character-section {
  margin-bottom: 12px;
}

.character-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  gap: 8px;
}

.character-name {
  color: #dbeafe;
  font-weight: bold;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 60px;
  text-align: left;
}

.spiritual-root-name {
  font-weight: bold;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 50px;
  text-align: center;
}

.character-sect {
  color: #60a5fa;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 60px;
  text-align: center;
}

.character-location {
  color: #94a3b8;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 40px;
  text-align: right;
}



.spiritual-root-single {
  color: #fbbf24;
}

.spiritual-root-mixed {
  color: #60a5fa;
}

.spiritual-root-waste {
  color: #ef4444;
}

.spiritual-root-default {
  color: #dbeafe;
}

.realm-progress-section {
  margin-top: 8px;
}

.realm-display {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.realm-text {
  color: #fbbf24;
  font-weight: bold;
  font-size: 16px;
  margin-right: 8px;
}

.period-text {
  color: #94a3b8;
  font-size: 13px;
}

.realm-bar {
  position: relative;
  height: 18px;
  background: #334155;
  border-radius: 9px;
  overflow: hidden;
  border: 1px solid #475569;
}

.realm-fill {
  height: 100%;
  background: linear-gradient(90deg, #fbbf24, #f59e0b);
  transition: width 0.3s ease;
}

.realm-value {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #1e293b;
  font-weight: bold;
}

/* Stats Section */
.stats-section {
  margin-bottom: 10px;
}

.stat-row {
  display: flex;
  gap: 6px;
  margin-bottom: 4px;
}

.stat-row:last-child {
  margin-bottom: 0;
}

.stat-item {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  line-height: 1.2;
}

.stat-item:has(.stat-bar) {
  flex: 1.5;
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
}

.stat-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.stat-label {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
}

.stat-value {
  color: #dbeafe;
  font-weight: bold;
  font-size: 13px;
}

.stat-bar {
  width: 100%;
  height: 10px;
  background: #334155;
  border-radius: 5px;
  overflow: hidden;
  border: 1px solid #475569;
}

.stat-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.qi-fill {
  background: #ef4444;
}

.mana-fill {
  background: #3b82f6;
}



/* Refining Section */
.refining-section {
  margin-bottom: 12px;
}

.refining-section h4 {
  margin: 0 0 6px 0;
  font-size: 13px;
  color: #34d399;
  border-bottom: 1px solid #334155;
  padding-bottom: 2px;
}

.refining-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.refining-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 6px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.refining-name {
  color: #dbeafe;
  font-size: 12px;
}

.refining-level {
  color: #fbbf24;
  font-size: 11px;
  font-weight: bold;
}

/* Forbidden Section */
.forbidden-section {
  margin-bottom: 12px;
}

.forbidden-section h4 {
  margin: 0 0 6px 0;
  font-size: 13px;
  color: #34d399;
  border-bottom: 1px solid #334155;
  padding-bottom: 2px;
}

.forbidden-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.forbidden-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 6px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.forbidden-label {
  color: #94a3b8;
  font-size: 12px;
}

.forbidden-value {
  color: #dbeafe;
  font-size: 12px;
  font-weight: 500;
}

/* Realm System Section */
.realm-system-section {
  margin-bottom: 12px;
}

.realm-system-section h4 {
  margin: 0 0 6px 0;
  font-size: 13px;
  color: #34d399;
  border-bottom: 1px solid #334155;
  padding-bottom: 2px;
}

.realm-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.realm-item {
  padding: 8px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.current-realm {
  border-color: #fbbf24;
  background: rgba(251, 191, 36, 0.1);
}

.realm-name {
  font-size: 13px;
  font-weight: bold;
  color: #dbeafe;
  margin-bottom: 6px;
}

.current-marker {
  color: #fbbf24;
  font-size: 12px;
  font-weight: normal;
}

.substage-progress {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
}

.substage-item {
  padding: 4px 6px;
  background: #334155;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.current-substage {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
}

.substage-name {
  color: #cbd5e1;
  font-size: 11px;
}

.progress-bar {
  position: relative;
  width: 80px;
  height: 12px;
  background: #475569;
  border-radius: 6px;
  overflow: hidden;
  margin: 0 4px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ef4444, #fbbf24);
  transition: width 0.3s ease;
}

.progress-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  color: #1e293b;
  font-weight: bold;
}

.completed {
  color: #16a34a;
  font-weight: bold;
  font-size: 10px;
}

.locked {
  color: #64748b;
  font-size: 10px;
}

.realm-completed {
  color: #16a34a;
  font-weight: bold;
  margin-top: 4px;
  font-size: 11px;
}

.realm-locked {
  color: #64748b;
  margin-top: 4px;
  font-size: 11px;
}



/* Status Badge */
.status-badge {
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: bold;
  margin-left: 4px;
}

.severe-injury-badge {
  background: #dc2626;
  color: white;
  border: 1px solid #dc2626;
  animation: pulse 2s infinite;
}

.injury-badge {
  background: rgba(220, 38, 38, 0.1); /* 参考原始上限框的浅红色 */
  color: #dc2626;
  border: 1px solid rgba(220, 38, 38, 0.3);
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}

/* Injury Info */
.injury-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  padding: 2px 4px;
  background: rgba(220, 38, 38, 0.1);
  border-radius: 3px;
  border: 1px solid rgba(220, 38, 38, 0.3);
}

.injury-text {
  color: #94a3b8;
  font-size: 10px;
}

.injury-reduction {
  color: #dc2626;
  font-size: 10px;
  font-weight: bold;
}





.close-btn {
  padding: 8px 16px;
  border: 1px solid #dc2626;
  background: transparent;
  color: #dc2626;
  cursor: pointer;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #dc2626;
  color: white;
}

/* 适配移动设备 */
@media (max-width: 600px) {
  .status-card {
    min-width: 90vw;
    max-width: 90vw;
  }

  .character-info-row {
    gap: 4px;
    padding: 6px 8px;
    font-size: 11px;
  }

  .character-name {
    flex: 1.5;
    min-width: 50px;
  }

  .spiritual-root-name {
    flex: 1.2;
    min-width: 40px;
  }

  .character-sect {
    flex: 1.5;
    min-width: 50px;
  }

  .character-location {
    flex: 1;
    min-width: 35px;
  }
}
</style>
